import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { Habit, DailyHabitCompletion } from '../../types';
import { CreateHabitResponse, MarkHabitCompletionResponse } from '../../types/responses';
import { toUUID, generateUUID } from '../../utils/uuidUtils';
import { API_ENDPOINTS, authenticatedFetch } from '../../config/api';

interface HabitsState {
  items: Habit[];
  completions: DailyHabitCompletion[];
  loading: boolean;
  error: string | null;
}

// Async thunk for fetching habits via API
const fetchHabits = createAsyncThunk(
  'habits/fetchHabits',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.HABITS.GET_ALL);

      if (!response.ok) {
        throw new Error(`Failed to fetch habits: ${response.statusText}`);
      }

      const habits: Habit[] = await response.json();
      return habits;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

// Async thunk for fetching habit completions via API
const fetchHabitCompletions = createAsyncThunk(
  'habits/fetchHabitCompletions',
  async (_, { rejectWithValue }) => {
    try {
      // Note: This is a placeholder; completions should be fetched per habit or with date range
      const response = await authenticatedFetch(API_ENDPOINTS.HABITS.GET_ALL);

      if (!response.ok) {
        throw new Error(`Failed to fetch habit completions: ${response.statusText}`);
      }

      const completions: DailyHabitCompletion[] = await response.json();
      return completions;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

// Async thunk for creating a habit
const createHabit = createAsyncThunk(
  'habits/createHabit',
  async (habitData: Omit<Habit, 'id' | 'userId' | 'createdAt' | 'streak' | 'bestStreak'>, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.HABITS.CREATE, {
        method: 'POST',
        body: JSON.stringify(habitData),
      });

      if (!response.ok) {
        throw new Error(`Failed to create habit: ${response.statusText}`);
      }

      const result: CreateHabitResponse = await response.json();
      return result;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

// Async thunk for fetching a specific habit
const getHabitById = createAsyncThunk(
  'habits/getHabitById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.HABITS.GET_BY_ID(id));

      if (!response.ok) {
        throw new Error(`Failed to fetch habit: ${response.statusText}`);
      }

      const habit: Habit = await response.json();
      return habit;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

// Async thunk for updating a habit
const updateHabitById = createAsyncThunk(
  'habits/updateHabitById',
  async ({ id, updates }: { id: string; updates: Partial<Habit> }, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.HABITS.UPDATE_BY_ID(id), {
        method: 'PUT',
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error(`Failed to update habit: ${response.statusText}`);
      }

      const habit: Habit = await response.json();
      return habit;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

// Async thunk for deleting a habit
const deleteHabitById = createAsyncThunk(
  'habits/deleteHabitById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.HABITS.DELETE_BY_ID(id), {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Failed to delete habit: ${response.statusText}`);
      }

      return id;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

// Async thunk for fetching habits with completions
const getHabitsWithCompletions = createAsyncThunk(
  'habits/getHabitsWithCompletions',
  async ({ startDate, endDate }: { startDate: string; endDate: string }, { rejectWithValue }) => {
    try {
      const url = `${API_ENDPOINTS.HABITS.GET_WITH_COMPLETIONS}?startDate=${startDate}&endDate=${endDate}`;
      const response = await authenticatedFetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch habits with completions: ${response.statusText}`);
      }

      const data = await response.json();
      return data; // Assume { habits: Habit[], completions: DailyHabitCompletion[] }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

// Async thunk for creating a completion
const createCompletion = createAsyncThunk(
  'habits/createCompletion',
  async ({ habitId, date }: { habitId: string; date: string }, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.HABITS.CREATE_COMPLETION(habitId), {
        method: 'POST',
        body: JSON.stringify({ date }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create completion: ${response.statusText}`);
      }

      const result: MarkHabitCompletionResponse = await response.json();
      return result; // Returns { completion, dailyBingoAwarded, weeklyBingoAwarded, pointsEarned }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

// Async thunk for fetching completions for a habit
const getCompletions = createAsyncThunk(
  'habits/getCompletions',
  async (habitId: string, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.HABITS.GET_COMPLETIONS(habitId));

      if (!response.ok) {
        throw new Error(`Failed to fetch completions: ${response.statusText}`);
      }

      const completions: DailyHabitCompletion[] = await response.json();
      return { habitId, completions };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

const initialState: HabitsState = {
  items: [],
  completions: [],
  loading: false,
  error: null,
};

const habitsSlice = createSlice({
  name: 'habits',
  initialState,
  reducers: {
    addHabit: (state, action: PayloadAction<Omit<Habit, 'id' | 'createdAt'>>) => {
      const newHabit: Habit = {
        ...action.payload,
        id: generateUUID(),
        createdAt: new Date().toISOString(),
      };
      state.items.push(newHabit);
    },
    updateHabit: (state, action: PayloadAction<{ id: string; updates: Partial<Habit> }>) => {
      const { id, updates } = action.payload;
      const habitIndex = state.items.findIndex(habit => habit.id === id);
      if (habitIndex !== -1) {
        state.items[habitIndex] = { ...state.items[habitIndex], ...updates };
      }
    },
    deleteHabit: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(habit => habit.id !== action.payload);
      state.completions = state.completions.filter(completion => completion.habitId !== action.payload);
    },
    toggleHabitCompletion: (state, action: PayloadAction<{ habitId: string; date: string }>) => {
      const { habitId, date } = action.payload;
      const existingCompletion = state.completions.find(
        c => c.habitId === habitId && c.date === date
      );

      if (existingCompletion) {
        existingCompletion.completed = !existingCompletion.completed;
        if (existingCompletion.completed) {
          existingCompletion.completedAt = new Date().toISOString();
        } else {
          existingCompletion.completedAt = undefined;
        }
      } else {
        state.completions.push({
          habitId: toUUID(habitId),
          date,
          completed: true,
          completedAt: new Date().toISOString(),
        });
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearHabits: (state) => {
      state.items = [];
      state.completions = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchHabits.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHabits.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchHabits.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchHabitCompletions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHabitCompletions.fulfilled, (state, action) => {
        state.loading = false;
        state.completions = action.payload;
      })
      .addCase(fetchHabitCompletions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createHabit.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createHabit.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
      })
      .addCase(createHabit.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getHabitById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getHabitById.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex(h => h.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        } else {
          state.items.push(action.payload);
        }
      })
      .addCase(getHabitById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateHabitById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateHabitById.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex(h => h.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateHabitById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteHabitById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteHabitById.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(h => h.id !== action.payload);
        state.completions = state.completions.filter(c => c.habitId !== action.payload);
      })
      .addCase(deleteHabitById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getHabitsWithCompletions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getHabitsWithCompletions.fulfilled, (state, action) => {
        state.loading = false;
        // Extract habits and flatten completions from each habit
        const habits = action.payload.map((habit: any) => ({
          ...habit,
          createdAt: habit.createdAt,
          completions: undefined // Remove completions from habit object
        }));
        const completions = action.payload.flatMap((habit: any) => habit.completions || []);
        state.items = habits;
        state.completions = completions;
      })
      .addCase(getHabitsWithCompletions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createCompletion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCompletion.fulfilled, (state, action) => {
        state.loading = false;
        const result = action.payload;
        const completion = result.completion;
        const index = state.completions.findIndex(c => c.habitId === completion.habitId && c.date === completion.date);
        if (index !== -1) {
          state.completions[index] = completion;
        } else {
          state.completions.push(completion);
        }
      })
      .addCase(createCompletion.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getCompletions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCompletions.fulfilled, (state, action) => {
        state.loading = false;
        // Replace completions for this habit
        state.completions = state.completions.filter(c => c.habitId !== action.payload.habitId);
        state.completions.push(...action.payload.completions);
      })
      .addCase(getCompletions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  addHabit,
  updateHabit,
  deleteHabit,
  toggleHabitCompletion,
  setLoading,
  setError,
  clearHabits
} = habitsSlice.actions;
export {
  fetchHabits,
  fetchHabitCompletions,
  createHabit,
  getHabitById,
  updateHabitById,
  deleteHabitById,
  getHabitsWithCompletions,
  createCompletion,
  getCompletions
};
export default habitsSlice.reducer;
